import {
  createAsyncThunk,
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import {
  fetchRoundingPolicyMasters,
  type RoundingPolicyMasters,
} from '@/services/tenant/policy-services';
import type { RootState } from '@/store';

/**
 * The empty shape every response is merged onto, so a list the API omits stays
 * an array rather than becoming undefined — and so each key keeps a stable
 * reference for selectors reading it.
 */
const EMPTY_MASTERS: RoundingPolicyMasters = {
  roundingTypes: [],
  fieldLevels: [],
  stageLevels: [],
  subStageLevels: [],
  consumerTypes: [],
  consumerSubTypes: [],
  reconcilStrategyTypes: [],
  consumerTypeFieldMappings: [],
};

export interface RoundingPolicyState {
  /** Lookup lists backing the policy wizard's dropdowns. */
  masters: RoundingPolicyMasters;
  loading: boolean;
  error: string | null;
}

const initialState: RoundingPolicyState = {
  masters: EMPTY_MASTERS,
  loading: false,
  error: null,
};

export const fetchRoundingPolicyMastersThunk = createAsyncThunk(
  'roundingPolicy/fetchMasters',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchRoundingPolicyMasters();
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to fetch policy lookups'
      );
    }
  }
);

const roundingPolicySlice = createSlice({
  name: 'roundingPolicy',
  initialState,
  reducers: {
    clearRoundingPolicyMasters(state) {
      state.masters = EMPTY_MASTERS;
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchRoundingPolicyMastersThunk.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchRoundingPolicyMastersThunk.fulfilled,
        (state, action: PayloadAction<Partial<RoundingPolicyMasters>>) => {
          state.loading = false;
          // Merged onto the empty shape so a key the API omits stays an array
          // rather than becoming undefined.
          state.masters = { ...EMPTY_MASTERS, ...action.payload };
        }
      )
      .addCase(fetchRoundingPolicyMastersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Something went wrong';
      });
  },
});

export const { clearRoundingPolicyMasters } = roundingPolicySlice.actions;

/**
 * Reads one lookup list by name, e.g.
 * `useSelector(selectPolicyMaster('subStageLevels'))`.
 *
 * Generic over the key so each list keeps its own element type — the bundle is
 * no longer uniformly `{ id, name }`. The list itself comes straight off the
 * store, so the reference is stable between unrelated store updates.
 */
export const selectPolicyMaster = <K extends keyof RoundingPolicyMasters>(
  key: K
) =>
  createSelector(
    (state: RootState) => state.tenant.roundingPolicy.masters,
    (masters): RoundingPolicyMasters[K] => masters[key] ?? EMPTY_MASTERS[key]
  );

/** The whole lookup bundle — used by the form/wire payload mapper. */
export const selectPolicyMasters = (state: RootState) =>
  state.tenant.roundingPolicy.masters;

export const selectRoundingPolicyLoading = (state: RootState) =>
  state.tenant.roundingPolicy.loading;

export default roundingPolicySlice.reducer;
